// Mein Tag / Meine Woche (Monteur-App, A-027 S3): die Termine, in denen der Monteur steht – mit allem, was er unterwegs braucht.
// Der zuletzt geladene Stand wird gemerkt (localStorage); ohne Netz bleibt der Tag lesbar: „Stand hh:mm – ohne Netz“.
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, Phone, Briefcase, ChevronRight, WifiOff, CheckCircle2, Clock, AlertTriangle, XCircle, User as UserIcon } from "lucide-react";
import { api } from "@/lib/api";
import VorOrtZeile from "@/components/VorOrtZeile"; // A-020 P4
import { colorForUser } from "@/lib/avatarUtils";

const CACHE_KEY = "monteur_plan_stand";
const STATUS = {
  wartet_auf_go: { label: "Wartet auf GO", cls: "bg-amber-100 text-amber-900 border-amber-300", icon: AlertTriangle },
  bestaetigt: { label: "Bestätigt", cls: "bg-blue-100 text-blue-900 border-blue-300", icon: Clock },
  im_kalender: { label: "Im Kalender", cls: "bg-emerald-100 text-emerald-900 border-emerald-300", icon: CheckCircle2 },
  erledigt: { label: "Erledigt", cls: "bg-emerald-100 text-emerald-900 border-emerald-300", icon: CheckCircle2 },
  abgesagt: { label: "Abgesagt", cls: "bg-red-100 text-red-700 border-red-200", icon: XCircle },
};
// Kein Netz: keine Antwort, oder der Service-Worker/Server meldet „nicht erreichbar“ (502/503/504)
export const istNetzFehler = (err) => !err?.response || [502, 503, 504].includes(err.response.status);
const z2 = (n) => String(n).padStart(2, "0");
const ymd = (d) => `${d.getFullYear()}-${z2(d.getMonth() + 1)}-${z2(d.getDate())}`;
const plusTage = (d, n) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
const hhmm = (s) => (s && s.length >= 16 ? s.slice(11, 16) : "");
const merke = (o) => { try { localStorage.setItem(CACHE_KEY, JSON.stringify(o)); } catch { /* ohne Speicher weiter */ } };
const gemerkt = () => { try { return JSON.parse(localStorage.getItem(CACHE_KEY) || "null"); } catch { return null; } };

const tagTitel = (iso, heute) => {
  const d = new Date(iso + "T00:00");
  const name = d.toLocaleDateString("de-DE", { weekday: "long", day: "numeric", month: "long" });
  if (iso === heute) return `Heute · ${name}`;
  if (iso === ymd(plusTage(new Date(heute + "T00:00"), 1))) return `Morgen · ${name}`;
  return name;
};

/** Karte eines Termins: Zeit, Kunde, Adresse mit Navigation, Telefon, Auftrag, Status; GO und erledigt wie bisher */
export const TerminKarte = ({ t, onOeffnen, onGo, onErledigt, ohneKnoepfe = false }) => {
  const st = STATUS[t.status] || STATUS.bestaetigt;
  const Icon = st.icon;
  const abgesagt = t.status === "abgesagt";
  const farbe = colorForUser((t.monteure || [])[0]?.username)?.hex || "#94a3b8";
  return (
    <div className={`bg-card border rounded-xl shadow-sm overflow-hidden ${abgesagt ? "opacity-60" : ""}`} style={{ borderLeft: `5px solid ${farbe}` }} data-testid={`plan-termin-${t.id}`}>
      <button type="button" onClick={onOeffnen} className="w-full text-left p-4 active:bg-muted/40" data-testid={`plan-oeffnen-${t.id}`}>
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="text-lg font-bold" data-testid={`plan-zeit-${t.id}`}>{hhmm(t.start) || "ganztägig"}{hhmm(t.ende) ? `–${hhmm(t.ende)}` : ""}</span>
              <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border inline-flex items-center gap-1 ${st.cls}`}><Icon className="w-3 h-3" />{st.label}</span>
            </div>
            <div className={`font-semibold text-base ${abgesagt ? "line-through" : ""}`}>{t.titel}</div>
            {t.kunde?.name && <div className="text-sm text-muted-foreground flex items-center gap-1 mt-0.5"><UserIcon className="w-3.5 h-3.5 shrink-0" />{t.kunde.name}</div>}
            {t.adresse && <div className="text-sm text-muted-foreground flex items-start gap-1 mt-0.5"><MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" /><span>{t.adresse}</span></div>}
            {t.auftrag && (t.auftrag.betreff || t.auftrag.beschreibung) && (
              <div className="mt-2 rounded-md bg-muted/50 px-2.5 py-1.5 text-sm" data-testid={`plan-auftrag-${t.id}`}>
                <div className="flex items-center gap-1 text-xs text-muted-foreground"><Briefcase className="w-3 h-3" />Auftrag</div>
                {t.auftrag.betreff && <div className="font-medium">{t.auftrag.betreff}</div>}
                {t.auftrag.beschreibung && <div className="text-muted-foreground line-clamp-2">{t.auftrag.beschreibung}</div>}
              </div>
            )}
            {(t.monteure || []).length > 1 && <div className="text-xs text-muted-foreground mt-1.5">Mit: {t.monteure.map((m) => m.name).join(", ")}</div>}
          </div>
          <ChevronRight className="w-5 h-5 text-muted-foreground mt-1 shrink-0" />
        </div>
      </button>
      {t.vor_ort && <div className="px-4 pb-2 -mt-1 text-muted-foreground"><VorOrtZeile kontakt={t.vor_ort} testId={`plan-vor-ort-${t.id}`} /></div>}
      {!ohneKnoepfe && !abgesagt && (
        <div className="flex gap-2 px-4 pb-3 flex-wrap">
          {t.navigation_url && (
            <a href={t.navigation_url} target="_blank" rel="noopener noreferrer" className="flex-1 min-w-[7rem] inline-flex items-center justify-center gap-1 py-2 rounded-lg bg-blue-500 text-white text-sm font-medium hover:bg-blue-600" data-testid={`plan-nav-${t.id}`}>
              <MapPin className="w-4 h-4" /> Navigation
            </a>
          )}
          {t.kunde?.telefon && (
            <a href={`tel:${t.kunde.telefon}`} className="flex-1 min-w-[7rem] inline-flex items-center justify-center gap-1 py-2 rounded-lg bg-emerald-500 text-white text-sm font-medium hover:bg-emerald-600" data-testid={`plan-tel-${t.id}`}>
              <Phone className="w-4 h-4" /> {t.kunde.telefon}
            </a>
          )}
          {onGo && t.status === "wartet_auf_go" && (
            <button type="button" onClick={onGo} className="flex-1 min-w-[7rem] py-2 rounded-lg bg-emerald-600 text-white text-sm font-bold hover:bg-emerald-700" data-testid={`plan-go-${t.id}`}>✓ GO</button>
          )}
          {onErledigt && (t.status === "bestaetigt" || t.status === "im_kalender") && (
            <button type="button" onClick={onErledigt} className="flex-1 min-w-[7rem] py-2 rounded-lg border border-emerald-600 text-emerald-700 text-sm font-semibold hover:bg-emerald-50" data-testid={`plan-erledigt-${t.id}`}>Erledigt</button>
          )}
        </div>
      )}
    </div>
  );
};

/** GO und „erledigt“ über die vorhandenen Routen */
export const useTerminAktionen = (nachher) => {
  const go = async (t) => {
    if (!window.confirm(`"${t.titel}" bestätigen?`)) return;
    try { await api.patch(`/module-termine/${t.id}/go`); toast.success("Termin bestätigt"); nachher?.(); }
    catch (err) { toast.error(err?.response?.data?.detail || "GO fehlgeschlagen"); }
  };
  const erledigt = async (t) => {
    const text = window.prompt(`Arbeitsbericht zu "${t.titel}": Was wurde gemacht / was fehlt noch?`, "");
    if (text === null) return;
    if (!text.trim()) { toast.error("Bitte kurz eintragen, was gemacht wurde"); return; }
    try { await api.patch(`/module-termine/${t.id}/erledigt`, { beschreibung: text.trim() }); toast.success("Als erledigt gemeldet"); nachher?.(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Konnte nicht als erledigt gemeldet werden"); }
  };
  return { go, erledigt };
};

export default function MeinPlan({ modus }) {
  const navigate = useNavigate();
  const [plan, setPlan] = useState(() => gemerkt());      // {stand, termine, heute}
  const [ohneNetz, setOhneNetz] = useState(false);
  const [laden, setLaden] = useState(true);
  const heute = ymd(new Date());

  const lade = useCallback(async () => {
    setLaden(true);
    try {
      const r = await api.get("/monteur/mein-plan", { params: { von: heute, bis: ymd(plusTage(new Date(), 6)) } });
      const neu = { stand: r.data.stand, termine: r.data.termine || [], heute, monteur: r.data.monteur };
      setPlan(neu); merke(neu); setOhneNetz(false);
    } catch (err) {
      if (istNetzFehler(err)) setOhneNetz(true);        // kein Netz: zuletzt geladenen Stand zeigen
      else toast.error(err.response?.data?.detail || "Plan konnte nicht geladen werden");
    } finally { setLaden(false); }
  }, [heute]);
  useEffect(() => { lade(); }, [lade]);
  useEffect(() => {
    const an = () => lade(); const aus = () => setOhneNetz(true);
    window.addEventListener("online", an); window.addEventListener("offline", aus);
    return () => { window.removeEventListener("online", an); window.removeEventListener("offline", aus); };
  }, [lade]);
  const { go, erledigt } = useTerminAktionen(lade);

  const alle = plan?.termine || [];
  const sichtbar = modus === "tag" ? alle.filter((t) => (t.start || "").slice(0, 10) === heute) : alle;
  const tage = [...new Set(sichtbar.map((t) => (t.start || "").slice(0, 10)))].sort();
  const standText = plan?.stand ? new Date(plan.stand).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit", timeZone: "Europe/Berlin" }) : "";
  const oeffnen = (t) => navigate(t.einsatz_id ? `/monteur/einsatz/${t.einsatz_id}` : `/monteur/termin/${t.id}`);

  return (
    <div data-testid={`mein-plan-${modus}`}>
      {ohneNetz && (
        <div className="mb-3 flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2 text-sm" data-testid="plan-ohne-netz">
          <WifiOff className="w-4 h-4 shrink-0" />
          <span>{plan ? `Stand ${standText} – ohne Netz` : "Ohne Netz – noch kein Stand gespeichert"}</span>
        </div>
      )}
      {!ohneNetz && plan && <p className="text-[11px] text-muted-foreground mb-2" data-testid="plan-stand">Stand {standText}</p>}
      {laden && !plan ? (
        <div className="text-center py-10 text-muted-foreground">Lade…</div>
      ) : tage.length === 0 ? (
        <div className="text-center py-14 text-muted-foreground" data-testid="plan-leer">
          <Clock className="w-12 h-12 mx-auto text-muted-foreground/40 mb-3" />
          {modus === "tag" ? "Heute keine Termine." : "In den nächsten 7 Tagen keine Termine."}
        </div>
      ) : (
        <div className="space-y-5">
          {tage.map((tag) => (
            <section key={tag} data-testid={`plan-tag-${tag}`}>
              {modus === "woche" && <h3 className="text-sm font-semibold mb-2 capitalize" data-testid={`plan-tag-titel-${tag}`}>{tagTitel(tag, heute)}</h3>}
              <div className="space-y-2">
                {sichtbar.filter((t) => (t.start || "").slice(0, 10) === tag).map((t) => (
                  <TerminKarte key={t.id} t={t} onOeffnen={() => oeffnen(t)} onGo={() => go(t)} onErledigt={() => erledigt(t)} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}
